import React from 'react'
import type { JSX } from 'react'

import { InterstitialButtonIcon } from './__internal__/interstitial-button-icon'
import { InterstitialButtonText } from './__internal__/interstitial-button-text'

import styles from './__internal__/interstitial-button.module.css'

/**
 * InterstitialButton Component
 *
 * A button component with intent-based styling and flexible icon/text layouts using compound components.
 *
 * Usage Examples:
 * // icon-left
 * <InterstitialButton intent={'primary'}>
 *   <InterstitialButton.Icon iconPath="/grove/icons/utility/arrow-right" />
 *   <InterstitialButton.Text>Call to Action</InterstitialButton.Text>
 * </InterstitialButton>
 *
 * // icon-right
 * <InterstitialButton intent={'primary'}>
 *   <InterstitialButton.Text>Call to Action</InterstitialButton.Text>
 *   <InterstitialButton.Icon iconPath="/grove/icons/utility/arrow-right" />
 * </InterstitialButton>
 *
 * // icon-only
 * <InterstitialButton intent={'primary'}>
 *   <InterstitialButton.Icon iconPath="/grove/icons/utility/arrow-right" />
 * </InterstitialButton>
 *
 * // text-only
 * <InterstitialButton intent={'primary'}>
 *   <InterstitialButton.Text>Call to Action</InterstitialButton.Text>
 * </InterstitialButton>
 */

type InterstitialButtonIntent = 'primary' | 'secondary' | 'transparent'

export function InterstitialButton({
  children,
  intent,
  ...buttonHTMLAttributes
}: {
  readonly children?: React.ReactNode
  readonly intent: InterstitialButtonIntent
} & Omit<
  JSX.IntrinsicElements['button'],
  // Omitted because not allowed:
  'className' | 'disabled' // Avoid 'disabled' since it's not accessibility friendly. Use 'aria-disabled' and 'readOnly' instead.
> &
  Required<Pick<JSX.IntrinsicElements['button'], 'aria-label'>>) {
  const className = `${styles.button} ${styles[intent]}`

  return (
    <button className={className} type={'button'} {...buttonHTMLAttributes}>
      {children}
    </button>
  )
}

InterstitialButton.Icon = InterstitialButtonIcon
InterstitialButton.Text = InterstitialButtonText
